import { createFileRoute } from '@tanstack/react-router';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';
import { signupUrl } from '../lib/urls.js';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

function AboutPage() {
  usePageTitle('About');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            About
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Good businesses lose time to{' '}
            <span className="text-brand-sage">bad booking tools.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            Bookaburra exists so service teams can take bookings without
            double-checking every room, person, and piece of equipment by hand.
          </p>
        </div>
      </section>

      {/* STORY */}
      <Section tone="shadow">
        <div className="grid gap-12 lg:grid-cols-[1fr_2fr] lg:gap-20">
          <div>
            <SectionEyebrow>Why we built it</SectionEyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              Your booking page should protect your team.
            </h2>
          </div>
          <div className="space-y-6 text-lg leading-relaxed text-brand-ink-soft/85">
            <p>
              Most booking tools ask one question: is the time free? Service
              businesses need more than that. The right room has to be free.
              The right person has to be working. Travel time has to fit.
            </p>
            <p>
              We built Bookaburra to catch those constraints before the client
              clicks book, not after your team has to clean it up.
            </p>
            <p>
              Australian built and supported. Your data is yours.
            </p>
          </div>
        </div>
      </Section>

      {/* PRINCIPLES */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>What you can rely on</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            No surprise costs. No lock-in. No silent failures.
          </h2>
        </div>
        <ul className="mt-12 grid gap-x-12 gap-y-10 sm:grid-cols-2">
          <Principle
            number="01"
            title="Know what you will pay"
            body="Clear plans, no setup fee, no per-booking fee, and no surprise add-ons."
          />
          <Principle
            number="02"
            title="Keep control of your data"
            body="Your bookings, clients, and settings are yours. Export them when you need them. Delete them when you are done."
          />
          <Principle
            number="03"
            title="Tell clients only what matters"
            body="Send the right confirmation, reminder, or change notice in the language the client picked."
          />
          <Principle
            number="04"
            title="Catch problems before they become bookings"
            body="Wrong room, missing skill, travel clash: Bookaburra blocks the slot before the client sees it."
          />
        </ul>
      </Section>

      {/* WHO */}
      <Section tone="shadow">
        <div className="grid gap-12 lg:grid-cols-[2fr_3fr] lg:items-start lg:gap-20">
          <div>
            <SectionEyebrow>Who built it</SectionEyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              One developer, 26 years in ecommerce.
            </h2>
          </div>
          <div className="space-y-5 text-lg leading-relaxed text-brand-ink-soft/85">
            <p>
              Bookaburra is designed and architected by{' '}
              <a
                href="https://www.linkedin.com/in/timgriffin"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-sage underline-offset-4 hover:underline"
              >
                Tim Griffin
              </a>
              {' '}— one developer with 26 years of ecommerce experience, built
              with AI coding assistance throughout.
            </p>
            <p>
              That matters because the architectural guardrails on{' '}
              <a
                href="/technology"
                className="text-brand-sage underline-offset-4 hover:underline"
              >
                the technology page
              </a>
              {' '}exist because the person who designed them has hit those
              failure modes in production before — not because a language model
              suggested them.
            </p>
          </div>
        </div>
      </Section>

      {/* BIRD */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-[2fr_3fr] lg:items-center lg:gap-20">
          <div>
            <SectionEyebrow>The name</SectionEyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              A local name for a practical job.
            </h2>
          </div>
          <div className="space-y-5 text-lg leading-relaxed text-brand-ink-soft/85">
            <p>
              Bookaburra combines bookings with a familiar Australian name. The
              icon pairs a kookaburra with a small calendar grid.
            </p>
            <p>
              The name is local. The reason is practical: make bookings less
              fragile for the teams doing the work.
            </p>
          </div>
        </div>
        <div className="mt-12 flex justify-center">
          <img
            src="/logos/bookaburra-icon.svg"
            alt="Bookaburra icon — kookaburra above a 2×2 calendar grid"
            className="h-32 w-auto"
          />
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-[3fr_2fr] lg:items-center">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
                See if it fixes your booking day.
              </h2>
              <p className="mt-6 max-w-xl text-lg text-brand-cream/80">
                Free forever. No card. Upgrade any time.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a
                href={signupUrl()}
                className="inline-flex items-center justify-center rounded-md bg-brand-cream px-6 py-3.5 text-sm font-medium text-brand-ink-soft hover:bg-brand-cream/90"
              >
                Start for free
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-md border border-brand-cream/30 px-6 py-3.5 text-sm font-medium text-brand-cream hover:bg-brand-cream/10"
              >
                Get in touch
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Principle({
  number,
  title,
  body,
}: {
  number: string;
  title: string;
  body: string;
}) {
  return (
    <li>
      <p className="text-sm font-semibold tracking-widest text-brand-sage">
        {number}
      </p>
      <h3 className="mt-2 text-xl font-bold tracking-tight text-brand-ink-soft">
        {title}
      </h3>
      <p className="mt-3 text-base text-brand-ink-soft/75">{body}</p>
    </li>
  );
}
